import type { ErrorState as ErrorStateType } from '#/index'
import iconBusy from '@/assets/images/icons/icon-busy.svg'
import iconClarify from '@/assets/images/icons/icon-clarify.svg'
import iconError from '@/assets/images/icons/icon-error.svg'
import iconReview from '@/assets/images/icons/icon-review.svg'
import './index.scss'

interface ErrorStateProps {
  error: ErrorStateType
  maxRetries?: number
  onRetry?: () => void
}

const MAX_RETRY_LIMIT = 3

/** 不可重试的错误类型：审核拦截、需求不明确、内容违规 — 只展示文案，不提供重试按钮 */
const NO_RETRY_TYPES = new Set<ErrorStateType['type']>(['content-review', 'need_clarify', 'rejected'])

const errorIconMap: Record<ErrorStateType['type'], string> = {
  'service-busy': iconBusy,
  network: iconError,
  'content-review': iconReview,
  need_clarify: iconClarify,
  rejected: iconError,
}

/** 错误状态展示组件 — 根据 error.type 显示对应图标、文案和重试按钮 */
export default function ErrorStateComponent({ error, onRetry }: ErrorStateProps) {
  const remainingRetries = Math.max(0, MAX_RETRY_LIMIT - error.retryCount)
  const canRetry = !NO_RETRY_TYPES.has(error.type) && remainingRetries > 0
  const isAutoRetry = error.type === 'service-busy' && error.countdown !== undefined && error.countdown > 0

  // error.message 格式为 "title\ndesc"，由 useStreamGeneration 的 catch 分支统一构造
  const [title, desc] = error.message.split('\n')

  return (
    <div className="error-state">
      <div className="error-state-inner">
        <div className="error-icon">
          <img src={errorIconMap[error.type]} alt="" />
        </div>
        <p className="error-title">{title}</p>
        <p className="error-desc">{desc}</p>
        {isAutoRetry && (
          <p className="error-retry-hint">
            {error.countdown} 秒后自动重试（剩余 {remainingRetries} 次）
          </p>
        )}
        {!isAutoRetry && canRetry && (
          <button className="retry-btn" onClick={onRetry}>
            重新生成（剩余 {remainingRetries} 次重试）
          </button>
        )}
        {!isAutoRetry && !canRetry && !NO_RETRY_TYPES.has(error.type) && (
          <p className="error-retry-hint">多次重试仍失败，建议检查配置后刷新页面</p>
        )}
      </div>
    </div>
  )
}
